// src/lib/training/feedbackQueue.ts
export type FeedbackReason = "no_hits" | "low_confidence" | "thumbs_down" | "error";

export type TrainingFeedbackEvent = {
  id: string;
  ts: number;
  version: string;
  deviceId: string;

  locationId?: string;
  locationName?: string;
  salespersonFirstName?: string;

  query: string;
  reason: FeedbackReason;

  topHitIds?: string[];
  topHitTitles?: string[];
  topHitScores?: number[];

  answerPreview?: string;
  feedbackText?: string;

  bb_tags?: string[];
  threadId?: string;
  threadTurn?: number;
  queryKind?: "new" | "followup";
};

const KEY_DEVICE_ID = "bb-device-id";
const KEY_QUEUE = "bb-training-feedback-queue";

const MAX_EVENTS = 200;
const MAX_TEXT = 420; // keep payload tight

const uuid = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

export function getOrCreateDeviceId(): string {
  try {
    const existing = localStorage.getItem(KEY_DEVICE_ID);
    if (existing) return existing;
    const created = `bb-${uuid()}`;
    localStorage.setItem(KEY_DEVICE_ID, created);
    return created;
  } catch {
    // If storage is blocked, still return something stable-ish for this session
    return `bb-session-${uuid()}`;
  }
}

// Basic PII stripping (keep it conservative; improves over time)
export function sanitizeText(input: string): string {
  if (!input) return "";

  let s = input;

  // emails
  s = s.replace(/\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi, "[email]");

  // phone-like digit runs (10+ digits, allow separators)
  s = s.replace(/(\+?1[\s.-]?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}\b/g, "[phone]");

  // long digit sequences (catch-all)
  s = s.replace(/\b\d{10,}\b/g, "[digits]");

  // collapse whitespace
  s = s.replace(/\s+/g, " ").trim();

  if (s.length > MAX_TEXT) s = s.slice(0, MAX_TEXT - 1) + "…";
  return s;
}

export function readFeedbackQueue(): TrainingFeedbackEvent[] {
  try {
    const raw = localStorage.getItem(KEY_QUEUE);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as TrainingFeedbackEvent[]) : [];
  } catch {
    return [];
  }
}

export function writeFeedbackQueue(events: TrainingFeedbackEvent[]) {
  try {
    localStorage.setItem(KEY_QUEUE, JSON.stringify(events));
  } catch {
    // ignore; device may be out of storage or blocked
  }
}

export function clearFeedbackQueue() {
  try {
    localStorage.removeItem(KEY_QUEUE);
  } catch {
    // ignore
  }
}

export function enqueueFeedbackEvent(
  partial: Omit<TrainingFeedbackEvent, "id" | "ts" | "deviceId">
) {
  const deviceId = getOrCreateDeviceId();

  const query = sanitizeText(partial.query || "");
  if (!query) return;

  const bb_tags =
    Array.isArray(partial.bb_tags)
      ? Array.from(
          new Set(
            partial.bb_tags
              .map((t) => String(t || "").trim())
              .filter(Boolean)
              .slice(0, 10)
          )
        )
      : undefined;

  const event: TrainingFeedbackEvent = {
    id: uuid(),
    ts: Date.now(),
    deviceId,
    ...partial,
    bb_tags,
    query,
    answerPreview: partial.answerPreview
      ? sanitizeText(partial.answerPreview)
      : undefined,
    feedbackText: partial.feedbackText
      ? sanitizeText(partial.feedbackText)
      : undefined,
  };

  const existing = readFeedbackQueue();

  // Dedup “repeat spam” lightly: same reason + same query (+ same tag signature) within 2 minutes
  const twoMin = 2 * 60 * 1000;

  const tagSig = (event.bb_tags || []).slice().sort().join("|");

  const isDupe = existing.some((e) => {
    const eTagSig = (e.bb_tags || []).slice().sort().join("|");
    return (
      e.reason === event.reason &&
      e.query === event.query &&
      eTagSig === tagSig &&
      Math.abs(e.ts - event.ts) < twoMin
    );
  });
  if (isDupe) return;

  const next = [event, ...existing].slice(0, MAX_EVENTS);
  writeFeedbackQueue(next);
}

export function enqueueKbGapEvent(args: {
  version: string;
  query: string;
  reason?: FeedbackReason; // default: "no_hits"
  answerPreview?: string; // what Buddy answered (optional)
  feedbackText?: string; // proposed answer / notes (optional)

  // Optional context (nice to have, never required)
  locationId?: string;
  locationName?: string;
  salespersonFirstName?: string;

  // Optional extra tags for filtering later (campaigns, marketing, etc.)
  extraTags?: string[];
}) {
  const tags = ["kb_gap", ...(args.extraTags ?? [])];

  enqueueFeedbackEvent({
    version: args.version,
    query: args.query,
    reason: args.reason ?? "no_hits",
    bb_tags: tags,
    answerPreview: args.answerPreview,
    feedbackText: args.feedbackText,
    locationId: args.locationId,
    locationName: args.locationName,
    salespersonFirstName: args.salespersonFirstName,
  });
}
